import { isValidElement } from "react";
import { Box, Link as MuiLink, Typography } from "@mui/material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import LearnVisual from "./LearnVisual";

function MarkdownLink({ href = "", children }) {
  const external = /^https?:\/\//i.test(href);
  return (
    <MuiLink
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      underline="hover"
      sx={{ fontWeight: 750 }}
    >
      {children}
    </MuiLink>
  );
}

function plainText(children) {
  if (typeof children === "string") return children;
  if (Array.isArray(children)) return children.map(plainText).join("");
  return "";
}

function formatTableValue(value) {
  const normalized = String(value).trim().toLowerCase();
  if (["high", "medium", "low"].includes(normalized)) {
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  }
  return value;
}

function formatTableChildren(children) {
  if (typeof children === "string") return formatTableValue(children);
  if (Array.isArray(children)) return children.map(formatTableChildren);
  return children;
}

function isRelevanceValue(children) {
  const normalized = plainText(children).trim().toLowerCase();
  return ["high", "medium", "low"].includes(normalized);
}

function isRelevanceHeader(children) {
  return plainText(children).trim().toLowerCase() === "relevance";
}

function childArray(children) {
  return Array.isArray(children) ? children : [children];
}

function hasLearnLink(children) {
  return childArray(children).some((child) => {
    if (!isValidElement(child)) return false;
    if (child.props?.href?.startsWith("/learn/")) return true;
    return hasLearnLink(child.props?.children);
  });
}

function isRelatedConceptLabel(children) {
  const meaningfulChildren = childArray(children).filter(
    (child) => typeof child !== "string" || child.trim()
  );
  if (meaningfulChildren.length !== 1) return false;
  const onlyChild = meaningfulChildren[0];
  return isValidElement(onlyChild) && onlyChild.type === "strong" && hasLearnLink(onlyChild.props.children);
}

function isContextItemLabel(children) {
  const meaningfulChildren = childArray(children).filter(
    (child) => typeof child !== "string" || child.trim()
  );
  if (meaningfulChildren.length !== 1) return false;
  const onlyChild = meaningfulChildren[0];
  if (!isValidElement(onlyChild) || onlyChild.type !== "strong") return false;
  if (hasLearnLink(onlyChild.props.children)) return true;
  const text = plainText(onlyChild.props.children).trim();
  return /^[A-Z0-9]/.test(text) && text.split(/\s+/).length <= 9;
}

function startsWithContextItemLabel(children) {
  const meaningfulChildren = childArray(children).filter(
    (child) => typeof child !== "string" || child.trim()
  );
  if (!meaningfulChildren.length) return false;
  const firstChild = meaningfulChildren[0];
  return isValidElement(firstChild) && firstChild.type === "strong" && isContextItemLabel([firstChild]);
}

function isCoreInvestorQuestionLabel(children) {
  return plainText(children).trim().toLowerCase() === "the core investor question is:";
}

function isPlainEnglishLabel(children) {
  return plainText(children).trim().toLowerCase() === "in plain english";
}

function isContextList(children) {
  return childArray(children).some((child) => {
    if (!isValidElement(child)) return false;
    const liChildren = childArray(child.props?.children);
    return liChildren.some((grandchild) => {
      if (!isValidElement(grandchild) || grandchild.type !== "p") return false;
      return startsWithContextItemLabel(grandchild.props?.children);
    });
  });
}

function splitAfterFirstH2Section(content) {
  const firstH2 = content.search(/^## /m);
  if (firstH2 === -1) return [content, ""];
  const nextH2 = content.slice(firstH2 + 1).search(/\n## /m);
  if (nextH2 === -1) return [content, ""];
  const splitIndex = firstH2 + 1 + nextH2 + 1;
  return [content.slice(0, splitIndex).trimEnd(), content.slice(splitIndex).trimStart()];
}

function MarkdownContent({ content }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        a: MarkdownLink,
        table({ children }) {
          return <Box className="learn-table-wrap"><table>{children}</table></Box>;
        },
        td({ children }) {
          const relevanceValue = isRelevanceValue(children);
          return (
            <Box
              component="td"
              className={relevanceValue ? "learn-table-relevance-cell" : undefined}
              sx={{ fontWeight: relevanceValue ? 650 : 400 }}
            >
              {formatTableChildren(children)}
            </Box>
          );
        },
        th({ children }) {
          return (
            <Box
              component="th"
              className={isRelevanceHeader(children) ? "learn-table-relevance-cell" : undefined}
            >
              {formatTableChildren(children)}
            </Box>
          );
        },
        p({ children }) {
          if (startsWithContextItemLabel(children) || isRelatedConceptLabel(children)) {
            return <p className="learn-context-item-label">{children}</p>;
          }
          if (isCoreInvestorQuestionLabel(children)) {
            return <p className="learn-core-question-label">{children}</p>;
          }
          if (isPlainEnglishLabel(children)) {
            return <p className="learn-plain-english-label">{children}</p>;
          }
          return <p>{children}</p>;
        },
        ul({ children }) {
          return <ul className={isContextList(children) ? "learn-context-list" : undefined}>{children}</ul>;
        },
        blockquote({ children }) {
          return <blockquote className="learn-core-question-callout">{children}</blockquote>;
        },
        h1({ children }) {
          return <Typography component="h2" variant="h4">{children}</Typography>;
        },
        h2({ children }) {
          return <h2>{children}</h2>;
        },
        h3({ children }) {
          return <h3>{children}</h3>;
        },
      }}
    >
      {content}
    </ReactMarkdown>
  );
}

export default function LearnArticleContent({ content, visual }) {
  if (!content) {
    return (
      <Typography variant="body1" color="text.secondary">
        This article does not have content yet.
      </Typography>
    );
  }

  const shouldRenderAfterDefinition = visual?.placement === "after_definition";
  const [definitionContent, remainingContent] = shouldRenderAfterDefinition
    ? splitAfterFirstH2Section(content)
    : [content, ""];

  return (
    <Box
      className="learn-article-content"
      sx={{
        color: "text.primary",
        fontSize: 16,
        lineHeight: 1.75,
        "& > *:first-child": { mt: 0 },
        "& > h2:first-of-type": {
          mt: { xs: 2.75, md: 3.25 },
        },
        "& h1": {
          mt: 3,
          mb: 1.25,
          fontSize: { xs: 25, md: 30 },
          lineHeight: 1.18,
          fontWeight: 900,
        },
        "& h2": {
          mt: { xs: 5, md: 5.75 },
          mb: { xs: 1.5, md: 1.75 },
          fontSize: { xs: 22, md: 26 },
          lineHeight: 1.22,
          fontWeight: 900,
        },
        "& h3": {
          mt: { xs: 3, md: 3.25 },
          mb: { xs: 1, md: 1.1 },
          fontSize: { xs: 19, md: 21 },
          lineHeight: 1.28,
          fontWeight: 850,
        },
        "& h4": {
          mt: 2.5,
          mb: 0.75,
          fontSize: 17,
          lineHeight: 1.3,
          fontWeight: 850,
        },
        "& p": { my: 0 },
        "& .learn-core-question-label": { mb: 1.35 },
        "& .learn-core-question-callout": {
          mt: 0,
          mb: { xs: 4, md: 4.75 },
        },
        "& .learn-plain-english-label": {
          mt: 0.75,
          mb: 0,
          pt: 1.15,
          px: 1.4,
          borderLeft: "3px solid rgba(201, 176, 123, 0.85)",
          borderTop: "1px solid rgba(34, 35, 37, 0.08)",
          borderRight: "1px solid rgba(34, 35, 37, 0.08)",
          borderTopRightRadius: 4,
          backgroundColor: "rgba(255, 255, 255, 0.58)",
          color: "text.secondary",
          fontSize: 11,
          lineHeight: 1.4,
          fontWeight: 850,
          letterSpacing: 0,
          textTransform: "uppercase",
        },
        "& .learn-plain-english-label + p": {
          mt: 0,
          mb: 1.25,
          px: 1.4,
          pb: 1.2,
          borderLeft: "3px solid rgba(201, 176, 123, 0.85)",
          borderRight: "1px solid rgba(34, 35, 37, 0.08)",
          borderBottom: "1px solid rgba(34, 35, 37, 0.08)",
          borderBottomRightRadius: 4,
          backgroundColor: "rgba(255, 255, 255, 0.58)",
          color: "text.primary",
        },
        "& p + blockquote": { mt: 1.25 },
        "& blockquote + h2": { mt: { xs: 4, md: 4.75 } },
        "& .learn-context-item-label": {
          mt: 0,
          mb: 0.35,
          fontSize: 15,
          lineHeight: 1.35,
          fontWeight: 400,
        },
        "& .learn-context-item-label > strong": {
          display: "block",
          mb: 0.35,
          fontWeight: 850,
        },
        "& p + .learn-context-item-label": {
          mt: 0,
        },
        "& .learn-context-item-label + p": {
          mt: 0.35,
        },
        "& .learn-context-list": { pl: 3, mt: 2, mb: 2.25 },
        "& .learn-context-list > li": { mb: 1.65 },
        "& .learn-context-list > li::marker": { color: "text.secondary" },
        "& .learn-context-list p": { my: 0 },
        "& .learn-context-list strong": {
          display: "block",
          mb: 0.35,
          fontWeight: 850,
        },
        "& .learn-context-item-label a": {
          color: "text.primary",
          textDecorationColor: "rgba(34, 35, 37, 0.25)",
          textUnderlineOffset: "3px",
          cursor: "pointer",
          transition: "color 140ms ease, text-decoration-color 140ms ease",
        },
        "& .learn-context-item-label a:hover": {
          color: "text.primary",
          textDecorationColor: "rgba(201, 176, 123, 0.85)",
        },
        "& p + p": { mt: 1.5 },
        "& ul + h2, & ol + h2": { mt: { xs: 4.5, md: 5.25 } },
        "& ul, & ol": { pl: 3, mt: 0, mb: 2 },
        "& p + ul, & p + ol": { mt: 2 },
        "& ul + p, & ol + p": { mt: 1.5 },
        "& p + h3": { mt: { xs: 4, md: 4.5 } },
        "& li": { mb: 0.55 },
        "& blockquote": {
          my: 2,
          mx: 0,
          pl: 2,
          borderLeft: "3px solid",
          borderColor: "primary.main",
          color: "text.secondary",
        },
        "& hr": {
          my: 3.5,
          border: 0,
          borderTop: "1px solid rgba(34, 35, 37, 0.14)",
        },
        "& img": {
          display: "block",
          maxWidth: "100%",
          height: "auto",
          my: 2.5,
          borderRadius: 1,
          border: "1px solid rgba(34, 35, 37, 0.10)",
          backgroundColor: "#fff",
        },
        "& code": {
          px: 0.55,
          py: 0.2,
          borderRadius: 0.75,
          backgroundColor: "rgba(34, 35, 37, 0.08)",
          fontSize: "0.92em",
        },
        "& pre": {
          my: 2,
          p: 2,
          overflowX: "auto",
          borderRadius: 1,
          backgroundColor: "rgba(34, 35, 37, 0.92)",
          color: "#fff",
        },
        "& pre code": {
          p: 0,
          backgroundColor: "transparent",
          color: "inherit",
        },
        "& table": {
          width: "100%",
          borderCollapse: "collapse",
          minWidth: "100%",
        },
        "& table th": {
          backgroundColor: "rgba(34, 35, 37, 0.06)",
          color: "text.primary",
          fontWeight: 900,
        },
        "& table th, & table td": {
          px: { xs: 1.4, md: 1.65 },
          py: { xs: 1.15, md: 1.35 },
          borderBottom: "1px solid rgba(34, 35, 37, 0.10)",
          textAlign: "left",
          verticalAlign: "top",
        },
        "& .learn-table-relevance-cell": {
          width: 116,
          whiteSpace: "nowrap",
        },
        "& table td:first-of-type": {
          fontWeight: 550,
        },
        "& .learn-table-wrap": {
          mt: 2,
          mb: 4,
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
          width: "100%",
          border: "1px solid rgba(34, 35, 37, 0.10)",
          borderRadius: 1,
          backgroundColor: "#fff",
        },
        "& .learn-callout": {
          my: 1.75,
          p: 2,
          borderRadius: 1,
          border: "1px solid rgba(34, 35, 37, 0.12)",
          backgroundColor: "rgba(255, 255, 255, 0.78)",
        },
        "& .learn-callout--info": {
          borderLeft: "4px solid rgba(201, 176, 123, 0.85)",
        },
        "& .learn-callout--warning": {
          borderLeft: "4px solid rgba(166, 92, 58, 0.85)",
        },
        "& .learn-callout--methodology": {
          borderLeft: "4px solid rgba(34, 35, 37, 0.65)",
        },
        "& .learn-callout--takeaway": {
          borderLeft: "4px solid rgba(92, 128, 91, 0.85)",
        },
      }}
    >
      <MarkdownContent content={definitionContent} />
      {shouldRenderAfterDefinition ? <LearnVisual visual={visual} /> : null}
      {remainingContent ? <MarkdownContent content={remainingContent} /> : null}
      {!shouldRenderAfterDefinition ? <LearnVisual visual={visual} /> : null}
    </Box>
  );
}
